import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { extractApiError } from "api/apiErrorHandler";
import { authUser, getUserDetails } from "api/usersApi";
import { showToast } from "components/toast";

const initialState = {
  token: localStorage.getItem("token"),
  loading: false,
  error: null,
  userLoading: false,
  userError: null,
  userInfo: {},
  permissions: {},
};

// Async login thunk
export const loginAsync = createAsyncThunk(
  "auth/login",
  async (data, thunkAPI) => {
    try {
      const response = await authUser({
        email_or_mobile: data?.email_or_mobile,
        password: data?.password,
      });

      const token = response?.token;
      if (token) {
        localStorage.setItem("token", token);
        await thunkAPI.dispatch(userDetails({ token }));
      }
      showToast.success(response?.msg || "Login successful!");
      return response;
    } catch (error) {
      const errMsg = extractApiError(error);
      console.log("errMsg: ", errMsg);
      showToast.error(errMsg || "Login failed");
      return thunkAPI.rejectWithValue(errMsg);
    }
  }
);

// Async user details thunk
export const userDetails = createAsyncThunk(
  "auth/userDetails",
  async (data, thunkAPI) => {
    try {
      return await getUserDetails(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.token = null;
      state.userInfo = {};
      state.permissions = {};
      localStorage.removeItem("token");
      showToast.info("Logged out successfully");
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        const token = action.payload?.token || "";
        const userData = action.payload?.data || {};

        state.token = token;
        state.userInfo = userData;
        state.loading = false;
        state.error = null;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // User Details
      .addCase(userDetails.pending, (state) => {
        state.userLoading = true;
        state.userError = null;
      })
      .addCase(userDetails.fulfilled, (state, action) => {
        const userData = action.payload?.data || {};

        state.userInfo = userData;
        state.permissions = userData?.roleId?.permissions;
        state.userLoading = false;
        state.userError = null;
      })
      .addCase(userDetails.rejected, (state, action) => {
        state.userLoading = false;
        state.userError = action.payload;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
